import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { getTeamPath } from "@/utils/team.utils";
import { getGamePath, getSportTheme } from "@/utils/game.utils";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { StatusBadge } from "@/components/ui/status-badge";
import { OpenMapsActions } from "@/components/maps/OpenMapsActions";
import type { StatusBadgeVariant } from "@/components/ui/status-badge";
import { Icon } from "@/components/ui/icon"

export interface GameCardProps {
  id: string;
  title: string;
  image: string;
  /** Prioridade alta para LCP (primeiros cards visíveis) */
  imagePriority?: boolean;
  date: string;
  location: string;
  status: "payment-due" | "interest-open" | "just-added" | "full";
  statusLabel: string;
  statusVariant: StatusBadgeVariant;
  currentPlayers: number;
  maxPlayers: number;
  actionLabel: string;
  actionVariant: "primary" | "secondary" | "outline" | "destructive";
  meta?: string;
  sport?: string;
  timeLeft?: string;
  countdown?: {
    days: number;
    hours: number;
    minutes: number;
    seconds?: number;
  };
  teamName?: string;
  teamId?: string;
  /** Slug do time para URL amigável (usa teamId como fallback) */
  teamSlug?: string;
  /** Foto do time (avatar) */
  teamPhoto?: string;
  onCardClick?: () => void;
  onAction?: () => void;
  actionIcon?: string;
  /** Exibe texto informativo no lugar do botão (ex.: check-in ainda não abriu). */
  actionPending?: boolean;
  /** Botão secundário abaixo da ação principal (ex.: ver detalhes). */
  secondaryAction?: {
    label: string;
    icon?: string;
    onClick: () => void;
  };
  hideAction?: boolean;
  hideProgress?: boolean;
  /** Exibe atalhos para Google Maps, Waze etc. abaixo do endereço. */
  mapsLocation?: {
    address: string;
    lat?: number | null;
    lng?: number | null;
  };
  price?: number;
  /** Selo contextual no hero do card (ex.: home — diferencia torneio de jogo). */
  entityLabel?: string;
  /** Ícone do selo (Material Symbols). Padrão: emoji_events (torneio). */
  entityIcon?: string;
  /** Classe completa do selo (fundo, texto, anel). */
  entityBadgeClassName?: string;
  /** Texto da barra de progresso (ex.: Equipes vs Jogadores). Padrão: Jogadores */
  progressUnitLabel?: string;
  /** Rótulo da categoria do esporte (ex.: “5 km”) sobre a foto. */
  sportCategoryLabel?: string;
  /** Exibe selo de acesso antecipado para quem é VIP/admin durante a janela exclusiva. */
  showVipAccess?: boolean;
}

export function GameCard({
  id,
  title,
  image,
  imagePriority = false,
  date,
  location,
  statusLabel,
  statusVariant,
  currentPlayers,
  maxPlayers,
  actionLabel,
  actionVariant,
  meta,
  sport,
  timeLeft,
  countdown,
  teamName,
  teamId,
  teamSlug,
  teamPhoto,
  status,
  onCardClick,
  onAction,
  actionIcon,
  hideAction = false,
  actionPending = false,
  secondaryAction,
  hideProgress = false,
  mapsLocation,
  price,
  entityLabel,
  entityIcon = "emoji_events",
  entityBadgeClassName = "bg-gradient-to-r from-amber-500 to-orange-500 text-white ring-1 ring-amber-200/60 shadow-sm shadow-amber-500/30",
  progressUnitLabel = "Jogadores",
  sportCategoryLabel,
  showVipAccess = false,
}: GameCardProps) {
  const navigate = useNavigate();
  const progressPercentage =
    maxPlayers > 0 ? (currentPlayers / maxPlayers) * 100 : 0;
  const isFull = status === "full";
  const theme = getSportTheme(sport, title);

  // Atualizar countdown em tempo real se fornecido
  const [currentCountdown, setCurrentCountdown] = useState(countdown);

  useEffect(() => {
    if (!countdown) {
      setCurrentCountdown(undefined);
      return;
    }

    // Atualizar a cada segundo para poder mostrar segundos quando faltar < 1 minuto
    const interval = setInterval(() => {
      setCurrentCountdown((prev) => {
        if (!prev) return undefined;

        let { days, hours, minutes, seconds = 0 } = prev;

        // Se já acabou
        if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
          return undefined;
        }

        if (seconds > 0) {
          seconds--;
        } else {
          if (minutes > 0 || hours > 0 || days > 0) {
            seconds = 59;
            if (minutes > 0) {
              minutes--;
            } else if (hours > 0) {
              hours--;
              minutes = 59;
            } else if (days > 0) {
              days--;
              hours = 23;
              minutes = 59;
            }
          } else {
            return undefined;
          }
        }

        return { days, hours, minutes, seconds };
      });
    }, 1000); // Atualizar a cada segundo

    return () => clearInterval(interval);
  }, [countdown]);

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick();
    } else {
      navigate(getGamePath(id));
    }
  };

  return (
    <div
      onClick={onCardClick ? handleCardClick : undefined}
      className={cn(
        "group flex flex-col overflow-hidden rounded-xl bg-card-surface backdrop-blur-sm border border-border shadow-xl shadow-primary/5 transition-all duration-300",
        "hover:scale-[1.02] hover:shadow-2xl hover:shadow-primary/10",
        onCardClick && "cursor-pointer")}
    >
      {/* Image Header - img com lazy loading nativo (background não suporta) */}
      <div className="relative h-48 w-full bg-muted overflow-hidden">
        <OptimizedImage
          src={image}
          alt={title}
          priority={imagePriority}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out",
            currentCountdown ? "grayscale" : "grayscale-0",
            "group-hover:scale-110")}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

        <div className="absolute top-3 left-3 z-[6] flex flex-col items-start gap-1.5 pointer-events-none">
          {entityLabel && (
            <div className="pointer-events-auto">
              <span
                className={cn(
                  "inline-flex items-center gap-0.5 rounded-full backdrop-blur-sm px-2 py-0.5 text-[9px] font-black uppercase tracking-wider",
                  entityBadgeClassName)}
              >
                <Icon name={entityIcon} filled />
                {entityLabel}
              </span>
            </div>
          )}

          {currentCountdown && (
            <div className="pointer-events-auto flex items-center gap-1 text-[10px] font-bold text-amber-500 bg-black/60 backdrop-blur-md px-2 py-1 rounded-full border border-white/10">
              <Icon name="schedule" />
              <span>
                {currentCountdown.days > 0 && `${currentCountdown.days}d `}
                {currentCountdown.hours}h {currentCountdown.minutes}m
                {currentCountdown.days === 0 &&
                  currentCountdown.hours === 0 &&
                  currentCountdown.minutes === 0 &&
                  typeof currentCountdown.seconds === "number" && (
                    <> {currentCountdown.seconds}s</>
                  )}
              </span>
            </div>
          )}
        </div>

        {/* Status Badge */}
        <div className="absolute top-3 right-3">
          <StatusBadge
            variant={statusVariant}
            appearance="overlay"
            size="sm"
            className="font-bold tracking-wide shadow-md"
          >
            {statusLabel}
          </StatusBadge>
        </div>

        <div className="absolute bottom-3 left-3 right-3 z-[5] flex flex-col gap-1.5 items-stretch pointer-events-none">
          {showVipAccess && (
            <div className="pointer-events-auto self-start">
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/90 backdrop-blur-md px-2.5 py-0.5 text-[10px] font-black uppercase tracking-wider text-white ring-1 ring-emerald-300/40 shadow-sm">
                <Icon name="workspace_premium" className="size-3.5" />
                Acesso VIP
              </span>
            </div>
          )}
          {sportCategoryLabel && (
            <div className="pointer-events-auto self-start max-w-[min(100%,18rem)]">
              <span className="inline-flex items-center rounded-md bg-black/70 backdrop-blur-sm px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white shadow-sm ring-1 ring-white/25">
                {sportCategoryLabel}
              </span>
            </div>
          )}
          {!currentCountdown && timeLeft && (
            <div className="pointer-events-auto self-start flex items-center gap-1.5 text-xs font-bold text-amber-300 bg-black/60 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/10">
              <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse shrink-0" />
              {timeLeft}
            </div>
          )}
          <h3 className="text-xl font-black leading-tight drop-shadow-md tracking-tight pointer-events-auto text-white">
            {title}
          </h3>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-5 gap-5">
        {/* Details */}
        {/* Details */}
        <div className="flex flex-col gap-3">
          {teamName && (
            <div
              onClick={(e) => {
                if (teamId || teamSlug) {
                  e.stopPropagation();
                  navigate(getTeamPath(teamSlug ?? teamId!));
                }
              }}
              className={cn(
                "flex items-center gap-3",
                "text-foreground",
                (teamId || teamSlug) && "cursor-pointer hover:text-primary transition-colors",
              )}
            >
              {teamPhoto ? (
                <OptimizedImage src={teamPhoto} alt={teamName} className="h-9 w-9 rounded-full object-cover shrink-0 ring-1 ring-border" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-muted/30 dark:bg-white/10 flex items-center justify-center shrink-0 text-muted-foreground dark:text-muted-foreground text-sm font-bold">
                  {teamName.charAt(0).toUpperCase()}
                </div>
              )}
              <span className="text-sm font-bold">{teamName}</span>
            </div>
          )}

          <div
            className={cn(
              "flex items-center gap-3",
              "text-foreground")}
          >
            <div
              className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center shrink-0",
                theme.bg,
                theme.text)}
            >
              <Icon name={theme.icon} />
            </div>
            <span className="text-sm font-medium">{theme.label}</span>
          </div>

          <div
            className={cn(
              "flex items-center gap-3",
              "text-foreground")}
          >
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
              <Icon name="calendar_month" />
            </div>
            <span className="text-sm font-medium">{date}</span>
          </div>

          <div
            className={cn(
              "flex items-center gap-3",
              "text-foreground")}
          >
            <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center shrink-0 text-muted-foreground">
              <Icon name="location_on" />
            </div>
            <span className="text-sm font-medium leading-snug">{location}</span>
          </div>

          {showVipAccess && (
            <div className="flex items-center gap-3 text-emerald-700 dark:text-emerald-400">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-500/10">
                <Icon name="workspace_premium" className="size-5" />
              </div>
              <span className="text-sm font-semibold">Acesso VIP</span>
            </div>
          )}

          <div
            className={cn(
              "flex items-center gap-3",
              "text-foreground")}
          >
            <div className="w-9 h-9 rounded-full bg-emerald-50 dark:bg-emerald-500/10 flex items-center justify-center shrink-0 text-emerald-600 dark:text-emerald-400">
              <Icon name="attach_money" className="size-5" />
            </div>
            <span className="text-sm font-medium">
              {price === 0 ? (
                <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                  Grátis
                </span>
              ) : (
                new Intl.NumberFormat("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                }).format(price || 0)
              )}
            </span>
          </div>
        </div>

        {/* Progress / Participantes */}
        {!hideProgress && (
          <div className="mt-auto pt-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold tracking-wider text-foreground">
                {maxPlayers === 0
                  ? `${currentPlayers} ${progressUnitLabel}`
                  : `${currentPlayers}/${maxPlayers} ${progressUnitLabel}`}
              </span>
              <span className="text-xs font-bold text-muted-foreground">
                {meta}
              </span>
            </div>
            {maxPlayers > 0 && (
            <div className="h-2.5 w-full rounded-full bg-muted overflow-hidden">
              <div
                className={cn(
                  "h-full rounded-full transition-all duration-1000 ease-out",
                  isFull
                    ? "bg-primary shadow-[0_0_10px_rgba(59,130,246,0.4)]"
                    : status === "interest-open"
                      ? "bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.4)]"
                      : status === "just-added"
                        ? "bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.4)]"
                        : "bg-primary shadow-[0_0_10px_rgba(59,130,246,0.4)]",
                )}
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            )}
          </div>
        )}

        {/* Action Button */}
        {!hideAction && (
          <div className="flex flex-col gap-2">
            {mapsLocation ? (
              <>
                <OpenMapsActions
                  address={mapsLocation.address}
                  lat={mapsLocation.lat}
                  lng={mapsLocation.lng}
                  variant="buttons"
                  stopPropagation
                />
                <div className="border-t border-border" role="separator" />
              </>
            ) : null}
            {actionPending ? (
              <div
                className="flex w-full items-center justify-center gap-2 rounded-xl min-h-12 px-4 py-3 text-sm font-semibold text-muted-foreground bg-muted/50 border border-border"
                role="status"
              >
                <Icon name={actionIcon || "schedule"} />
                <span className="text-center leading-snug">{actionLabel}</span>
              </div>
            ) : (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onAction) {
                    onAction();
                  }
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl h-12 px-6 text-base font-bold leading-normal tracking-wide transition-all",
                  actionVariant === "primary" &&
                    "bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/25 active:scale-95",
                  actionVariant === "secondary" &&
                    "bg-muted hover:bg-surface-hover text-foreground",
                  actionVariant === "outline" &&
                    "bg-transparent border-2 border-border text-foreground hover:bg-muted",
                  actionVariant === "destructive" &&
                    "bg-red-50 dark:bg-red-900/10 hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400")}
              >
                <span>{actionLabel}</span>
                <Icon name={actionIcon ||
                    (actionVariant === "destructive" ? "close" : "arrow_forward")} />
              </button>
            )}
            {secondaryAction ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  secondaryAction.onClick();
                }}
                className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl h-12 px-6 text-base font-bold leading-normal tracking-wide transition-all bg-transparent border-2 border-border text-foreground hover:bg-muted active:scale-95"
              >
                <span>{secondaryAction.label}</span>
                <Icon name={secondaryAction.icon || "arrow_forward"} />
              </button>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}
